import { computed, reactive, toValue } from 'vue';

import {
  getPlannedAssetVisits,
  getResolvedAssetVisits,
  getUnplannedAndUnresolvedAssetVisits
} from '@nova/core';

import { groupArrivalsByDate, sortArrivalsNewestFirst } from './arrivalDisplay';

import type { MaybeRefOrGetter } from 'vue';
import type { Maybe } from '@nova/core';
import type { Arrival } from './types';

export interface UseArrivalsDisplayOptions {
  timezone: MaybeRefOrGetter<Maybe<string>>;
  locale: MaybeRefOrGetter<string>;
}

function countUnacknowledged(arrivals: Arrival[]): number {
  return arrivals.filter(arrival => !arrival.checkInAcknowledged).length;
}

/** Splits arrivals into the drawer's tabs. No fetching: pass the fetcher's `data`. */
export function useArrivalsDisplay(
  arrivals: MaybeRefOrGetter<Arrival[]>,
  options: UseArrivalsDisplayOptions
) {
  const sortedArrivals = computed(() => sortArrivalsNewestFirst(toValue(arrivals)));

  const pendingArrivals = computed(
    () => getUnplannedAndUnresolvedAssetVisits(sortedArrivals.value) as Arrival[]
  );
  const plannedArrivals = computed(() => getPlannedAssetVisits(sortedArrivals.value) as Arrival[]);
  const resolvedArrivals = computed(
    () => getResolvedAssetVisits(sortedArrivals.value) as Arrival[]
  );

  const groupByDate = (tabArrivals: Arrival[]) =>
    groupArrivalsByDate(tabArrivals, toValue(options.timezone), toValue(options.locale));

  const pendingGroups = computed(() => groupByDate(pendingArrivals.value));
  const plannedGroups = computed(() => groupByDate(plannedArrivals.value));
  const resolvedGroups = computed(() => groupByDate(resolvedArrivals.value));

  // Resolved arrivals need no acknowledgment, so they never ask for attention
  const unacknowledgedPendingCount = computed(() => countUnacknowledged(pendingArrivals.value));
  const unacknowledgedPlannedCount = computed(() => countUnacknowledged(plannedArrivals.value));
  const attentionCount = computed(
    () => unacknowledgedPendingCount.value + unacknowledgedPlannedCount.value
  );

  const hasArrivals = computed(() => sortedArrivals.value.length > 0);

  return reactive({
    hasArrivals,
    attentionCount,

    pendingArrivals,
    plannedArrivals,
    resolvedArrivals,

    pendingGroups,
    plannedGroups,
    resolvedGroups,

    unacknowledgedPendingCount,
    unacknowledgedPlannedCount
  });
}

export type ArrivalsDisplayData = ReturnType<typeof useArrivalsDisplay>;
